import { QueryKey, useQuery } from '@tanstack/react-query'

import { peachApi } from 'core/api'
import * as R from 'core/helpers/remeda'
import { OperationData } from 'core/types/makeMutationHook'
import {
  OperationResponse,
  PaginatedOperation,
  QueryHookArguments,
  getQueryOptions,
} from 'core/types/makeQueryHook'
import {
  internalEmployeesGet,
  listCaseType,
  listCasesByBorrower,
  loansGet,
  supercaseList,
} from 'core/types/operations'

const fetchAllPages = async <Resp extends PaginatedOperation>(
  page: Resp,
): Promise<Resp> => {
  if (!page.nextUrl) {
    return page
  }

  const resp: Resp = await peachApi.get({ url: page.nextUrl })
  const nextPage = await fetchAllPages(resp)
  const data = R.concat(page.data ?? [], nextPage.data ?? [])
  return { ...nextPage, data, count: data.length }
}

const makeFetchAllHook =
  <
    Resp extends PaginatedOperation,
    Key extends QueryKey,
    Vars extends { query?: unknown },
  >(
    data: OperationData<Resp, Key, Vars>,
  ) =>
  (args: QueryHookArguments<Resp, Key, Vars>): OperationResponse<Resp> => {
    const options = getQueryOptions(
      data,
      R.mergeDeep({ query: { limit: 100 } }, args) as typeof args,
    )

    const result = useQuery({
      ...options,
      queryFn: async () => fetchAllPages(await options.queryFn()),
    })

    return (
      result.data && typeof result.data === 'object' && 'data' in result.data
        ? {
            ...result,
            data: result.data.data,
            pagination: R.pick(result.data as any, [
              'total',
              'count',
              'nextUrl',
              'previousUrl',
            ]),
          }
        : result
    ) as any
  }

export const useCasesListAll = makeFetchAllHook(listCasesByBorrower)

export const useSupercasesListAll = makeFetchAllHook(supercaseList)

export const useCaseTypeListAll = makeFetchAllHook(listCaseType)

export const useLoansGetAll = makeFetchAllHook(loansGet)

export const useEmployeesGetAll = makeFetchAllHook(internalEmployeesGet)
